package com.neurobs.ccabserver.translation.states

import com.neurobs.ccab.db.{ProductVersionData, TranslationData}
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{GenericState, GenericStateGenerator, ServerDataAttribute, StateAttribute}
import org.scalajs.dom
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.useState
import slinky.core.facade.{React, ReactElement, ReactRef}

case class TranslationReplacement(
  originalText: String,
  translationData: TranslationData
                              ) {
  import TranslationReplacement.makeReplacements
  def isChanged = originalText != translationData.text
  def replace(subPhrase: String, replacement: String) = {
    val replaced = makeReplacements(translationData.text, subPhrase, replacement)
    val newTranslationData = translationData.copy(text = replaced)
    TranslationReplacement(originalText, newTranslationData)
  }
  def revert = TranslationReplacement(originalText, translationData.copy(text = originalText))
  def dbId = translationData.id
}

object TranslationReplacement {
  def lower(s: String) = s.toLowerCase
  def capital(s: String) = s.capitalize
  def makeReplacements(string: String, target: String, replacement: String) = {
    val replaced = string.replaceAll(lower(target), lower(replacement)).replaceAll(capital(target), capital(replacement))
    replaced
  }
  def apply(translationData: TranslationData): TranslationReplacement = TranslationReplacement(translationData.text, translationData)
}

case class TranslationUpdateProgress(
  totalUpdates: Int,
  totalSuccesses: Int,
  errors: Seq[String]
                                    ) {
  def addSuccess = this.copy(totalSuccesses = totalSuccesses + 1)
  def addError(error: String) = this.copy(errors = errors ++ Seq(error))
  def isComplete = totalSuccesses + errors.length == totalUpdates
}

object TranslationSubSearchState {
  val minReplacementLength = 0
}

case class TranslationSubSearchState (
  productVersionState: StateAttribute[Option[ProductVersionData]],
  subPhraseState: StateAttribute[String],
  replacementPhraseState: StateAttribute[String],
  matchingTranslationsState: ServerDataAttribute[Seq[TranslationReplacement]],
  focusedTranslationIdState: StateAttribute[Long],
  updateProgressState: StateAttribute[Option[TranslationUpdateProgress]]
                                     ) extends GenericState {

  def minReplacementLength = TranslationSubSearchState.minReplacementLength

  def getProductVersion(withProductVersion: ProductVersionData => Unit): Unit = {
    TranslationServer.getProductVersions(1)(ServerResponseUtil.handleObjectResultResponse[Seq[ProductVersionData]](
      handleObject = versions => {
        val latestVersion = versions.sortBy(_.version).reverse.head
        productVersionState.setValue(Some(latestVersion))
        withProductVersion(latestVersion)
      }
    ))
  }

  def searchTranslations(translatorId: Long, subPhrase: String) = {
    subPhraseState.setValue(subPhrase)
    def search(productVersion: ProductVersionData): Unit = {
      TranslationServer.searchUserTranslationsBySubPhrase(productVersion.id, translatorId, subPhrase)(handleServerMatchingTranslations(subPhrase))
    }
    productVersionState.value match {
      case Some(productVersion) => search(productVersion)
      case None => getProductVersion(search(_))
    }
  }

  def handleServerMatchingTranslations(requiredWord: String) = ServerResponseUtil.handleObjectResultResponse[Seq[TranslationData]](
    handleObject = translations => {
      setMatchingTranslations(translations.map(TranslationReplacement(_)), requiredWord)
    },
    onAnyError = matchingTranslationsState.setError(_)
  )


  def setMatchingTranslations(translationReplacements: Seq[TranslationReplacement], requiredWord: String): Unit = {
    matchingTranslationsState.setData(translationReplacements)
    focusedTranslationIdState.setValue(-1)
  }

  def replaceInFocusedTranslation(subPhrase: String, replacement: String) = {
    replacementPhraseState.setValue(replacement)
    matchingTranslationsState.withData[Unit](translations => {
      val replaced = translations.map(translation => {
        if (translation.dbId == focusedTranslationIdState.value) translation.replace(subPhrase, replacement)
        else translation
      })
      matchingTranslationsState.setData(replaced)
    }, Unit)
  }

  def replaceInAllTranslations(subPhrase: String, replacement: String) = {
    replacementPhraseState.setValue(replacement)
    matchingTranslationsState.withData[Unit](translations => {
      val replaced = translations.map(_.replace(subPhrase, replacement))
      matchingTranslationsState.setData(replaced)
    }, Unit)
  }

  def revertFocused() = {
    matchingTranslationsState.withData[Unit](translations => {
      val replaced = translations.map(translation => {
        if (translation.dbId == focusedTranslationIdState.value) translation.revert
        else translation
      })
      matchingTranslationsState.setData(replaced)
    }, Unit)
  }

  def revertAll() = {
    matchingTranslationsState.withData[Unit](translations => {
      val replaced = translations.map(_.revert)
      matchingTranslationsState.setData(replaced)
    }, Unit)
  }

  def updateHelper(remaining: Seq[TranslationReplacement], progress: TranslationUpdateProgress): Unit = {
    def onComplete = ServerResponseUtil.handleObjectResultResponse[TranslationData](
      handleObject = translation => {
        val updatedProgress = alterUpdateProgress(Right(translation), progress)
        updateHelper(remaining.tail, updatedProgress)
      },
      onAnyError = error => {
        val updatedProgress = alterUpdateProgress(Left(error), progress)
        updateHelper(remaining.tail, updatedProgress)
      }
    )
    remaining.headOption match {
      case None => Unit
      case Some(translation) =>
        TranslationServer.updateUserTranslation(translation.translationData)(onComplete)
    }
  }

  def updateAllTranslations() = {
    matchingTranslationsState.withData[Unit](translations => {
      val progress = initUpdateProcess(translations.length)
      updateHelper(translations, progress)
    }, Unit)
  }

  def initUpdateProcess(totalUpdates: Int) = {
    val progress = TranslationUpdateProgress(totalUpdates, 0, Seq())
    updateProgressState.setValue(Some(progress))
    progress
  }
  def alterUpdateProgress(result: Either[String, TranslationData], progress: TranslationUpdateProgress) = {
    val updated = result match {
      case Left(error) => progress.addError(error)
      case Right(_) => progress.addSuccess
    }
    updateProgressState.setValue(Some(updated))
    updated
  }
}

object TranslationSubSearchStateGenerator extends GenericStateGenerator[TranslationSubSearchState] {
  override def generateEmptyDefaultState(): TranslationSubSearchState = TranslationSubSearchState(
    productVersionState = emptyOptionDefaultState[ProductVersionData](),
    subPhraseState = emptyDefaultState(""),
    replacementPhraseState = emptyDefaultState(""),
    matchingTranslationsState = emptyServerDataState[Seq[TranslationReplacement]](),
    focusedTranslationIdState = emptyDefaultState(-1),
    updateProgressState = emptyOptionDefaultState[TranslationUpdateProgress]()
  )
}

@react
object TranslationSubSearchStateProvider {
  case class Props(children: ReactElement*)
  val component = FunctionalComponent[Props] { props =>

    val productVersionState = StateAttribute(useState[Option[ProductVersionData]](None))
    val subPhraseState = StateAttribute(useState(""))
    val replacementPhraseState = StateAttribute(useState(""))
    val matchingTranslationsState = ServerDataAttribute(useState[Either[Option[String], Seq[TranslationReplacement]]](Left(None)))
    val focusedTranslationIdState = StateAttribute(useState[Long](-1))
    val updateProgressState = StateAttribute(useState[Option[TranslationUpdateProgress]](None))

    val translationSubSearchState = TranslationSubSearchState(
      productVersionState = productVersionState,
      subPhraseState = subPhraseState,
      replacementPhraseState = replacementPhraseState,
      matchingTranslationsState = matchingTranslationsState,
      focusedTranslationIdState = focusedTranslationIdState,
      updateProgressState = updateProgressState
    )

    TranslationSubSearchStateGenerator.Provider(translationSubSearchState)(
      props.children:_*
    )
  }
}


